import FileUpload from "../../FileUpload/FileUpload";
import FileUploadControls from "../../FileUploadControls/FileUploadControls";
import { ImageUploadDisplayRoot, SingleImageUploadRoot } from "./SingleImageUpload.styles";
import { Image } from "@commonlibs";
import { UploadInputDisplaySize } from "../../FileUpload/UploadInputDisplay/UploadInputDisplay.types";
import ImageUploadPlaceholder from "../ImageUploadPlaceholder/ImageUploadPlaceholder";
import ImageDrag from "../ImageDrag/ImageDrag";
import { SingleImageUploadProps } from "./SingleImageUpload.types";
import useSingleImageUpload from "./hook/useSingleImageUpload";
import clsx from "clsx";

export const DEFAULT_SIZE_WITH_CONTROLLERS: UploadInputDisplaySize[] = ["lg", "xl"];

function SingleImageUpload<Multiple extends boolean = false>({
    onChange,
    value: valueProp,
    className,
    style,
    multiple,
    upload,
    onError,
    onSuccess,
    isLoading: isLoadingProp,
    defaultValue,
    readonly,
    disabled,
    size = "lg",
    fullWidth,
    showControllers = DEFAULT_SIZE_WITH_CONTROLLERS.includes(size) && fullWidth,
    controllersPosition = fullWidth ? "internal" : "external",
    alt,
    changeButtonProps,
    uploadButtonProps,
    showDelete,
    ...rest
}: SingleImageUploadProps<Multiple>) {
    /**
     * Hook to handle all the logic of the SingleImageUpload
     * It shares the state between the display and the controls and manages the uploads
     */
    const { value, error, loading, success, ...fileUploadProps } = useSingleImageUpload<Multiple>({
        onChange,
        value: valueProp,
        defaultValue,
        onError,
        onSuccess,
    });

    //Component variables
    const isLoading = isLoadingProp || loading;
    const showImageDisplay = typeof value === "string" || !value;
    const showIntControllers = showControllers && controllersPosition === "internal";
    const showExtControllers = showControllers && controllersPosition === "external";
    const showPlaceholder = !isLoading && !showIntControllers;

    //Inner components
    const commonUploadProps = {
        disabled: disabled || isLoading,
        readonly,
        fileTypes: "image/*",
        multiple,
        upload,
        value,

        ...fileUploadProps,
    };
    const controllerComponent = (
        <FileUploadControls
            {...commonUploadProps}
            {...{
                buttonSize: DEFAULT_SIZE_WITH_CONTROLLERS.includes(size) ? "lg" : "md",
                uploadButtonProps,
                changeButtonProps,
                showDelete,
            }}
        />
    );
    const placeholder = <>{showPlaceholder && <ImageUploadPlaceholder size={size} />} </>;

    return (
        <SingleImageUploadRoot size={size} className={clsx("single-image-upload", className)} style={style}>
            <ImageUploadDisplayRoot
                className={clsx({ "full-width": fullWidth, "is-loading": isLoading })}
                showInternalControllers={showIntControllers}
            >
                <FileUpload
                    size={size}
                    fullWidth={fullWidth}
                    isError={error}
                    isLoading={loading}
                    isSuccess={success}
                    drag={<ImageDrag size={size} />}
                    placeholder={placeholder}
                    {...rest}
                    {...commonUploadProps}
                >
                    {showImageDisplay && <Image alt={alt} src={value || ""} key={value} />}
                </FileUpload>
                {showIntControllers && controllerComponent}
            </ImageUploadDisplayRoot>
            {showExtControllers && controllerComponent}
        </SingleImageUploadRoot>
    );
}

export default SingleImageUpload;
